/**
 * Admin API Service - Detachable Admin Module
 * 
 * This service handles all API calls for the admin module.
 * When extracting to a separate app, this file can be moved with minimal changes.
 * Only the import path for the base API client would need to be updated.
 */

import api from '../../utils/axios';
import { isValidEmail } from '../../utils/validators';
import type {
  UserListParams,
  UserListResponse,
  UserCreditsResponse,
  CreditHistoryParams,
  CreditHistoryResponse,
  AddCreditsRequest,
  AddCreditsResponse,
  ChangePlanRequest,
  ChangePlanResponse,
  UpdateLimitsRequest,
  UpdateLimitsResponse,
  BillingConfigResponse,
  UserIdentifier
} from '../types';

// ================================
// RUNNING APPS TYPES
// ================================

export interface RunningApp {
  project_id: string;
  project_title: string;
  owner_email: string;
}

// ================================
// BASE ADMIN API CLASS
// ================================

class AdminApiService {
  private readonly baseUrl = '/admin';

  // ================================
  // USER MANAGEMENT
  // ================================

  /**
   * List all platform users with billing information
   */
  async listUsers(params: UserListParams = {}): Promise<UserListResponse> {
    const response = await api.get(`${this.baseUrl}/platform-users`, { params });
    return response.data;
  }

  /**
   * Get detailed credit information for a specific user
   */
  async getUserCredits(userIdentifier: UserIdentifier): Promise<UserCreditsResponse> {
    const response = await api.get(`${this.baseUrl}/platform-users/${encodeURIComponent(userIdentifier)}/credits`);
    return response.data;
  }

  /**
   * Get all projects for a specific user
   */
  async getUserProjects(userIdentifier: UserIdentifier): Promise<import('../types').UserProjectsResponse> {
    const response = await api.get(`${this.baseUrl}/platform-users/${encodeURIComponent(userIdentifier)}/projects`);
    return response.data;
  }


  // ================================
  // CREDIT MANAGEMENT
  // ================================

  /**
   * Add credits to a specific user
   */
  async addCreditsToUser(
    userIdentifier: UserIdentifier,
    request: AddCreditsRequest
  ): Promise<AddCreditsResponse> {
    const response = await api.post(
      `${this.baseUrl}/platform-users/${encodeURIComponent(userIdentifier)}/add-credits`,
      request
    );
    return response.data;
  }

  // ================================
  // PLAN MANAGEMENT
  // ================================

  /**
   * Change a user's billing plan
   */
  async changeUserPlan(
    userIdentifier: UserIdentifier,
    request: ChangePlanRequest
  ): Promise<ChangePlanResponse> {
    const response = await api.post(
      `${this.baseUrl}/platform-users/${encodeURIComponent(userIdentifier)}/change-plan`,
      request
    );
    return response.data;
  }

  // ================================
  // BILLING CONFIGURATION (Future)
  // ================================

  /**
   * Update user limits for monthly plans
   */
  async updateUserLimits(
    userIdentifier: UserIdentifier,
    request: UpdateLimitsRequest
  ): Promise<UpdateLimitsResponse> {
    const response = await api.post(
      `${this.baseUrl}/platform-users/${encodeURIComponent(userIdentifier)}/update-limits`,
      request
    );
    return response.data;
  }

  // ================================
  // BILLING CONFIGURATION
  // ================================

  /**
   * Get billing system configuration
   */
  async getBillingConfig(): Promise<BillingConfigResponse> {
    const response = await api.get(`${this.baseUrl}/billing/config`);
    return response.data;
  }

  /**
   * Update billing system configuration
   */
  async updateBillingConfig(updates: Record<string, any>): Promise<any> {
    const response = await api.put(`${this.baseUrl}/billing/config`, updates);
    return response.data;
  }

  // ================================
  // SYSTEM OPERATIONS (Future)
  // ================================

  /**
   * Process credit resets
   */
  async processResets(): Promise<any> {
    const response = await api.post(`${this.baseUrl}/system/process-resets`);
    return response.data;
  }

  /**
   * Get admin billing health check
   */
  async getBillingHealth(): Promise<any> {
    const response = await api.get(`${this.baseUrl}/billing/health`);
    return response.data;
  }

  // ================================
  // UTILITY METHODS
  // ================================

  /**
   * Search users by query string
   */
  async searchUsers(query: string, limit: number = 20): Promise<UserListResponse> {
    return this.listUsers({ search: query, limit });
  }

  /**
   * Get users by plan type
   */
  async getUsersByPlan(planType: string, limit: number = 50): Promise<UserListResponse> {
    // Note: This would require backend support for plan filtering
    return this.listUsers({ limit });
  }

  /**
   * Get users with low credits (less than threshold)
   */
  async getUsersWithLowCredits(threshold: number = 10): Promise<UserListResponse> {
    // Note: This would require backend support for credit filtering
    return this.listUsers({});
  }

  // ================================
  // RUNNING APPLICATIONS
  // ================================

  /**
   * Get all currently running project applications (admin only).
   * Projects not found in the database are excluded from the response.
   */
  async getRunningApps(): Promise<RunningApp[]> {
    const response = await api.get(`${this.baseUrl}/running-apps`);
    return response.data;
  }

  // ================================
  // USER CREATION
  // ================================

  /**
   * Create a new user on behalf of the admin.
   * The account is created with email pre-verified and the admin's temporary
   * password, which the user must replace on first sign-in. No email is sent —
   * the admin relays the password themselves.
   */
  async createUser(data: {
    email: string;
    firstName: string;
    lastName: string;
    organizationName?: string;
    password: string;
  }): Promise<{
    message: string;
    user_id: string;
    email: string;
    created_by: string;
    created_at: number;
  }> {
    const response = await api.post(`${this.baseUrl}/create-user`, data);
    return response.data;
  }
}

// ================================
// SINGLETON INSTANCE
// ================================

export const adminApi = new AdminApiService();

// ================================
// CONVENIENCE FUNCTIONS
// ================================

/**
 * Convenience function to check if a user exists
 */
export const checkUserExists = async (userIdentifier: UserIdentifier): Promise<boolean> => {
  try {
    await adminApi.getUserCredits(userIdentifier);
    return true;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return false;
    }
    throw error;
  }
};

/**
 * Convenience function to format user display name
 */
export const formatUserDisplayName = (user: {
  firstName?: string;
  lastName?: string;
  username?: string;
  email: string;
}): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  if (user.username) {
    return user.username;
  }
  return user.email;
};

/**
 * Convenience function to format credit amounts
 */
export const formatCreditAmount = (amount: number): string => {
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  }
  return `$${amount.toFixed(2)}`;
};

/**
 * Convenience function to format plan type for display
 */
export const formatPlanType = (planType: string): string => {
  switch (planType.toLowerCase()) {
    case 'starter':
      return 'Starter';
    case 'weekly':
      return 'Weekly';
    case 'monthly':
      return 'Monthly';
    case 'unlimited':
      return 'Unlimited';
    default:
      return planType.charAt(0).toUpperCase() + planType.slice(1);
  }
};

/**
 * Convenience function to get plan type color
 */
export const getPlanTypeColor = (planType: string): string => {
  switch (planType.toLowerCase()) {
    case 'starter':
      return 'bg-gray-100 text-gray-800';
    case 'weekly':
      return 'bg-blue-100 text-blue-800';
    case 'monthly':
      return 'bg-green-100 text-green-800';
    case 'unlimited':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Convenience function to get account status color
 */
export const getAccountStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-800';
    case 'suspended':
      return 'bg-red-100 text-red-800';
    case 'inactive':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// ================================
// ERROR HANDLING UTILITIES
// ================================

/**
 * Handle admin API errors consistently
 */
export const handleAdminApiError = (error: any): string => {
  if (error.response?.data?.detail) {
    // Ensure detail is converted to string (handle both string and object cases)
    const detail = error.response.data.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    // FastAPI validation errors (422) come back as a list of issue objects —
    // surface the first readable message instead of a raw JSON blob.
    if (Array.isArray(detail)) {
      const messages = detail
        .map((issue: any) => (typeof issue === 'string' ? issue : issue?.msg))
        .filter((msg: any): msg is string => typeof msg === 'string' && msg.length > 0)
        .map((msg: string) => msg.replace(/^Value error,\s*/i, ''));
      if (messages.length > 0) {
        return messages[0];
      }
      return 'Invalid request data. Please check your input.';
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || 'Invalid request data. Please check your input.';
    }
    return String(detail);
  }
  if (error.response?.status === 403) {
    return 'Admin access required. Please contact your administrator.';
  }
  if (error.response?.status === 404) {
    return 'User not found.';
  }
  if (error.response?.status === 422) {
    return 'Invalid request data. Please check your input.';
  }
  if (error.response?.status === 503) {
    return 'Service temporarily unavailable. Please try again later.';
  }
  return error.message || 'An unexpected error occurred.';
};

/**
 * Validate user identifier format
 */
export const validateUserIdentifier = (identifier: string): boolean => {
  // Check if it's an email
  if (isValidEmail(identifier)) {
    return true;
  }
  
  // Check if it's a UUID (Keycloak user ID)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(identifier)) {
    return true;
  }
  
  // Check if it's a valid username (alphanumeric + some special chars)
  const usernameRegex = /^[a-zA-Z0-9._-]+$/;
  if (usernameRegex.test(identifier) && identifier.length >= 3) {
    return true;
  }
  
  return false;
};

// ================================
// EXPORT DEFAULT
// ================================

export default adminApi;

